/**
 * Document Submission Page
 * 직원 기본 제출 서류 페이지 - 필수/선택 서류 업로드 (드래그 & 드롭 + 다수 파일 지원)
 */

import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  FileUp,
  FileCheck,
  Clock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  Upload,
  RefreshCw,
  ArrowRight,
  FileText,
  Ban,
  Hourglass,
  Download,
  Files,
} from "lucide-react";
import requiredDocumentService, {
  type RequiredDocumentSubmission,
  type SubmittedFileItem,
} from "@/services/requiredDocumentService";
import { useAuthStore } from "@/stores/authStore";
import { format, parseISO } from "date-fns";
import { isPastDueDate } from "@/lib/utils";
import { ko } from "date-fns/locale";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case "pending":
      return (
        <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
          <Clock className="w-3 h-3 mr-1" />
          미제출
        </Badge>
      );
    case "submitted":
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50">
          <FileUp className="w-3 h-3 mr-1" />
          제출완료
        </Badge>
      );
    case "approved":
      return (
        <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          승인
        </Badge>
      );
    case "rejected":
      return (
        <Badge variant="destructive" className="bg-red-100 text-red-700 border-red-300">
          <XCircle className="w-3 h-3 mr-1" />
          반려
        </Badge>
      );
    case "exemption_requested":
      return (
        <Badge variant="outline" className="text-orange-600 border-orange-300 bg-orange-50">
          <Hourglass className="w-3 h-3 mr-1" />
          면제 심사중
        </Badge>
      );
    case "exempted":
      return (
        <Badge variant="outline" className="text-purple-600 border-purple-300 bg-purple-50">
          <ShieldCheck className="w-3 h-3 mr-1" />
          면제
        </Badge>
      );
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

function DocumentCard({
  submission,
  onSubmit,
  onResubmit,
  onWithdraw,
  onRequestExemption,
  onCancelExemptionRequest,
  isUploading,
}: {
  submission: RequiredDocumentSubmission;
  onSubmit: (templateId: number, files: File[]) => void;
  onResubmit: (submissionId: number, files: File[]) => void;
  onWithdraw: (submissionId: number) => void;
  onRequestExemption: (submission: RequiredDocumentSubmission) => void;
  onCancelExemptionRequest: (submissionId: number) => void;
  isUploading: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const [action, setAction] = useState<"submit" | "resubmit" | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const allowMultiple = submission.allow_multiple_files;

  // 일자 기준 — 마감일 당일은 지연이 아님
  const isOverdue =
    isPastDueDate(submission.deadline_date) &&
    !["approved", "exempted", "submitted", "exemption_requested"].includes(submission.status);

  const canUpload = ["pending", "rejected"].includes(submission.status);

  const processFiles = useCallback((fileList: File[], uploadAction: "submit" | "resubmit") => {
    const validFiles = fileList.filter((f) => f.size <= 50 * 1024 * 1024);
    if (validFiles.length === 0) return;

    const filesToUpload = allowMultiple ? validFiles : [validFiles[0]];

    if (uploadAction === "resubmit") {
      onResubmit(submission.id, filesToUpload);
    } else {
      onSubmit(submission.template_id, filesToUpload);
    }
  }, [allowMultiple, submission.id, submission.template_id, onSubmit, onResubmit]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !action) return;
    processFiles(files, action);
    setAction(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const triggerUpload = (uploadAction: "submit" | "resubmit") => {
    setAction(uploadAction);
    fileInputRef.current?.click();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (canUpload && dragCounterRef.current === 1) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    if (!canUpload || isUploading) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const uploadAction = submission.status === "rejected" ? "resubmit" : "submit";
    processFiles(files, uploadAction);
  };

  // Display submitted files (prefer new `files` array, fallback to legacy single file)
  const submittedFiles: SubmittedFileItem[] =
    submission.files && submission.files.length > 0
      ? submission.files
      : submission.file_name
        ? [{ path: "", name: submission.file_name, size: submission.file_size || undefined, mime_type: undefined }]
        : [];

  // Form files for download
  const formFiles = submission.form_files && submission.form_files.length > 0
    ? submission.form_files
    : submission.form_file_name
      ? [{ path: "", name: submission.form_file_name }]
      : [];

  return (
    <Card
      className={`relative transition-colors ${isOverdue ? "border-red-300 bg-red-50/30" : ""} ${
        isDragging ? "border-primary border-2 bg-primary/5" : ""
      }`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileSelect}
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.hwp,.hwpx,.xlsx,.xls"
        multiple={allowMultiple}
      />

      {/* Drag overlay */}
      {isDragging && canUpload && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary/10 rounded-lg border-2 border-dashed border-primary">
          <div className="text-center">
            <Upload className="w-8 h-8 mx-auto text-primary mb-2" />
            <p className="text-sm font-medium text-primary">
              {allowMultiple ? "파일을 여기에 놓으세요 (다수 가능)" : "파일을 여기에 놓으세요"}
            </p>
          </div>
        </div>
      )}

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {submission.is_mandatory ? (
                <Badge variant="destructive" className="text-xs shrink-0">필수</Badge>
              ) : (
                <Badge variant="secondary" className="text-xs shrink-0">선택</Badge>
              )}
              <StatusBadge status={submission.status} />
              {allowMultiple && (
                <Badge variant="outline" className="text-xs shrink-0">
                  <Files className="w-3 h-3 mr-1" />
                  다수파일
                </Badge>
              )}
            </div>
            <CardTitle className="text-base mt-2">{submission.template_name}</CardTitle>
          </div>
        </div>
        {submission.deadline_date && (
          <CardDescription className={isOverdue ? "text-red-500 font-medium" : ""}>
            {isOverdue ? "마감일 초과: " : "마감일: "}
            {format(parseISO(submission.deadline_date), "yyyy년 M월 d일", { locale: ko })}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {/* 양식 다운로드 */}
          {formFiles.length > 0 && (
            <div className="space-y-1">
              {formFiles.map((ff, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  className="w-full text-blue-600 border-blue-200 hover:bg-blue-50 justify-start"
                  onClick={async () => {
                    try {
                      const result = await requiredDocumentService.getFormDownloadUrl(
                        submission.template_id,
                        ff.path || undefined
                      );
                      window.open(result.download_url, "_blank");
                    } catch {
                      // silently fail
                    }
                  }}
                >
                  <Download className="w-4 h-4 mr-2 shrink-0" />
                  <span className="truncate">양식: {ff.name}</span>
                </Button>
              ))}
            </div>
          )}

          {/* 제출 파일 정보 */}
          {submittedFiles.length > 0 && ["submitted", "approved"].includes(submission.status) && (
            <div className="space-y-1">
              {submittedFiles.map((f, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-md p-2">
                  <FileText className="w-4 h-4 shrink-0" />
                  <span className="truncate">{f.name}</span>
                  {f.size && (
                    <span className="text-xs shrink-0">({formatFileSize(f.size)})</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 제출일 */}
          {submission.submitted_at && (
            <p className="text-xs text-muted-foreground">
              제출일: {format(parseISO(submission.submitted_at), "yyyy.MM.dd HH:mm")}
            </p>
          )}

          {/* 반려 사유 */}
          {submission.status === "rejected" && submission.reject_reason && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3">
              <p className="text-sm font-medium text-red-700 mb-1">반려 사유</p>
              <p className="text-sm text-red-600">{submission.reject_reason}</p>
            </div>
          )}

          {/* 면제 요청 대기 */}
          {submission.status === "exemption_requested" && (
            <div className="rounded-md bg-orange-50 border border-orange-200 p-3">
              <p className="text-sm font-medium text-orange-700 mb-1">면제 요청 사유</p>
              <p className="text-sm text-orange-600">{submission.exempt_reason}</p>
              <p className="text-xs text-orange-500 mt-2">관리자 승인을 기다리고 있습니다.</p>
            </div>
          )}

          {/* 면제 완료 사유 */}
          {submission.status === "exempted" && submission.exempt_reason && (
            <div className="rounded-md bg-purple-50 border border-purple-200 p-3">
              <p className="text-sm font-medium text-purple-700 mb-1">면제 사유</p>
              <p className="text-sm text-purple-600">{submission.exempt_reason}</p>
            </div>
          )}

          {/* pending 상태 */}
          {submission.status === "pending" && (
            <>
              <div
                className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                onClick={() => triggerUpload("submit")}
              >
                {isUploading ? (
                  <RefreshCw className="w-6 h-6 mx-auto text-muted-foreground mb-1 animate-spin" />
                ) : (
                  <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-1" />
                )}
                <p className="text-sm font-medium">
                  {allowMultiple ? "파일 선택 또는 드래그 (다수 가능)" : "파일 선택 또는 드래그"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF, DOC, HWP, JPG, PNG 등 (최대 50MB)
                </p>
              </div>
              {!submission.is_mandatory && (
                <Button
                  className="w-full"
                  variant="outline"
                  size="sm"
                  onClick={() => onRequestExemption(submission)}
                  disabled={isUploading}
                >
                  <Ban className="w-4 h-4 mr-2" />
                  해당없음 (면제 요청)
                </Button>
              )}
            </>
          )}

          {/* rejected 상태 */}
          {submission.status === "rejected" && (
            <>
              <div
                className="border-2 border-dashed border-red-300 rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                onClick={() => triggerUpload("resubmit")}
              >
                {isUploading ? (
                  <RefreshCw className="w-6 h-6 mx-auto text-muted-foreground mb-1 animate-spin" />
                ) : (
                  <RefreshCw className="w-6 h-6 mx-auto text-muted-foreground mb-1" />
                )}
                <p className="text-sm font-medium">재제출 - 파일 선택 또는 드래그</p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF, DOC, HWP, JPG, PNG 등 (최대 50MB)
                </p>
              </div>
              {!submission.is_mandatory && (
                <Button
                  className="w-full"
                  variant="outline"
                  size="sm"
                  onClick={() => onRequestExemption(submission)}
                  disabled={isUploading}
                >
                  <Ban className="w-4 h-4 mr-2" />
                  해당없음 (면제 요청)
                </Button>
              )}
            </>
          )}

          {/* 면제 요청 중 - 취소 버튼 */}
          {submission.status === "exemption_requested" && (
            <Button
              className="w-full"
              variant="outline"
              size="sm"
              onClick={() => onCancelExemptionRequest(submission.id)}
            >
              면제 요청 취소
            </Button>
          )}

          {submission.status === "submitted" && (
            <>
              <p className="text-xs text-center text-muted-foreground">검토 대기 중</p>
              <Button
                className="w-full"
                variant="outline"
                size="sm"
                onClick={() => onWithdraw(submission.id)}
                disabled={isUploading}
              >
                <XCircle className="w-4 h-4 mr-2" />
                제출 철회 (다시 올리기)
              </Button>
            </>
          )}

          {submission.status === "approved" && (
            <p className="text-xs text-center text-green-600">승인 완료</p>
          )}

          {submission.status === "exempted" && (
            <p className="text-xs text-center text-purple-600">제출 면제</p>
          )}
      </CardContent>
    </Card>
  );
}

export default function DocumentSubmissionPage({ onSkip }: { onSkip?: () => void } = {}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { clearDocumentSubmissionRequired, getDefaultLandingPath, isPastDeadline, hasActiveExtension } = useAuthStore();

  const [exemptionTarget, setExemptionTarget] = useState<RequiredDocumentSubmission | null>(null);
  const [exemptionReason, setExemptionReason] = useState("");
  const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);
  const [extensionReason, setExtensionReason] = useState("");

  // Fetch all submissions
  const { data: submissions = [], isLoading } = useQuery({
    queryKey: ["my-document-submissions"],
    queryFn: requiredDocumentService.getMyAll,
  });

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: ({ templateId, files }: { templateId: number; files: File[] }) =>
      requiredDocumentService.submitDocument(templateId, files),
    onSuccess: () => {
      toast({ title: "서류가 제출되었습니다." });
      queryClient.invalidateQueries({ queryKey: ["my-document-submissions"] });
    },
    onError: (error: Error) => {
      toast({ title: "제출 실패", description: error.message, variant: "destructive" });
    },
  });

  // Resubmit mutation
  const resubmitMutation = useMutation({
    mutationFn: ({ submissionId, files }: { submissionId: number; files: File[] }) =>
      requiredDocumentService.resubmitDocument(submissionId, files),
    onSuccess: () => {
      toast({ title: "서류가 재제출되었습니다." });
      queryClient.invalidateQueries({ queryKey: ["my-document-submissions"] });
    },
    onError: (error: Error) => {
      toast({ title: "재제출 실패", description: error.message, variant: "destructive" });
    },
  });

  // Exemption request mutation
  const exemptionRequestMutation = useMutation({
    mutationFn: ({ submissionId, reason }: { submissionId: number; reason: string }) =>
      requiredDocumentService.requestExemption(submissionId, reason),
    onSuccess: () => {
      toast({ title: "면제 요청이 접수되었습니다." });
      queryClient.invalidateQueries({ queryKey: ["my-document-submissions"] });
      setExemptionTarget(null);
      setExemptionReason("");
    },
    onError: (error: Error) => {
      toast({ title: "면제 요청 실패", description: error.message, variant: "destructive" });
    },
  });

  // Cancel exemption request mutation
  const cancelExemptionMutation = useMutation({
    mutationFn: (submissionId: number) =>
      requiredDocumentService.cancelExemptionRequest(submissionId),
    onSuccess: () => {
      toast({ title: "면제 요청이 취소되었습니다." });
      queryClient.invalidateQueries({ queryKey: ["my-document-submissions"] });
    },
    onError: (error: Error) => {
      toast({ title: "취소 실패", description: error.message, variant: "destructive" });
    },
  });

  // Withdraw submission mutation
  const withdrawMutation = useMutation({
    mutationFn: (submissionId: number) =>
      requiredDocumentService.withdrawSubmission(submissionId),
    onSuccess: () => {
      toast({ title: "제출이 철회되었습니다. 다시 업로드해 주세요." });
      queryClient.invalidateQueries({ queryKey: ["my-document-submissions"] });
    },
    onError: (error: Error) => {
      toast({ title: "철회 실패", description: error.message, variant: "destructive" });
    },
  });

  // Extension request mutation
  const extensionMutation = useMutation({
    mutationFn: (reason: string) => requiredDocumentService.requestExtension(reason),
    onSuccess: () => {
      toast({ title: "1일 연장이 요청되었습니다.", description: "건너뛰기가 가능해졌습니다." });
      setExtensionDialogOpen(false);
      setExtensionReason("");
      // Refresh mandatory check to update extension state
      useAuthStore.getState().initialize();
    },
    onError: (error: Error) => {
      toast({ title: "연장 요청 실패", description: error.message, variant: "destructive" });
    },
  });

  const isUploading = submitMutation.isPending || resubmitMutation.isPending || withdrawMutation.isPending;

  // 완료(approved/exempted) 제외, 진행 중인 것만 표시
  const activeDocs = submissions.filter(
    (s) => !["approved", "exempted"].includes(s.status)
  );
  const mandatoryDocs = activeDocs.filter((s) => s.is_mandatory);
  const optionalDocs = activeDocs.filter((s) => !s.is_mandatory);

  // Check if all mandatory documents are done (submitted/approved/exempted/exemption_requested) - 전체 submissions 기준
  // exemption_requested도 제출 완료로 취급 (관리자가 거절하면 pending으로 복원되어 다시 온보딩 표시됨)
  const allMandatoryDone = submissions
    .filter((s) => s.is_mandatory)
    .every((s) => ["submitted", "approved", "exempted", "exemption_requested"].includes(s.status));

  const handleContinue = async () => {
    // Re-check mandatory status from server
    try {
      const result = await requiredDocumentService.checkMandatory();
      if (!result.has_pending_mandatory) {
        clearDocumentSubmissionRequired();
        const defaultPath = getDefaultLandingPath();
        navigate(defaultPath, { replace: true });
      } else {
        toast({
          title: "필수 서류 미제출",
          description: "모든 필수 서류를 제출해야 시스템을 이용할 수 있습니다.",
          variant: "destructive",
        });
      }
    } catch {
      toast({ title: "확인 실패", description: "잠시 후 다시 시도해주세요.", variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const pendingTotalCount = submissions.filter(
    (s) => s.status === "pending" || s.status === "rejected"
  ).length;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* 건너뛰기 / 연장 요청 */}
        <div className="flex justify-end gap-2">
          {isPastDeadline && !hasActiveExtension ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExtensionDialogOpen(true)}
            >
              <Clock className="w-4 h-4 mr-1" />
              1일 연장 요청
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (onSkip) {
                  onSkip();
                } else {
                  const defaultPath = getDefaultLandingPath();
                  navigate(defaultPath, { replace: true });
                }
              }}
            >
              건너뛰기 <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>

        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <FileCheck className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">기본 제출 서류</h1>
          <p className="text-muted-foreground">
            아래 서류를 기한 내에 제출해 주세요.
          </p>
        </div>

        {/* Summary */}
        {submissions.length > 0 && (
          <Card className={pendingTotalCount > 0 ? "border-amber-300 bg-amber-50/50" : "border-green-300 bg-green-50/50"}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                {pendingTotalCount > 0 ? (
                  <>
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        미제출 서류가 {pendingTotalCount}건 있습니다.
                      </p>
                      <p className="text-xs text-amber-600">
                        모든 필수 서류를 제출해야 시스템을 이용할 수 있습니다.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        모든 필수 서류가 제출되었습니다.
                      </p>
                      <p className="text-xs text-green-600">
                        아래 버튼을 눌러 시스템으로 진입하세요.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mandatory Documents */}
        {mandatoryDocs.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              필수 서류
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {mandatoryDocs.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  submission={doc}
                  onSubmit={(templateId, files) => submitMutation.mutate({ templateId, files })}
                  onResubmit={(submissionId, files) => resubmitMutation.mutate({ submissionId, files })}
                  onWithdraw={(id) => withdrawMutation.mutate(id)}
                  onRequestExemption={() => {}}
                  onCancelExemptionRequest={() => {}}
                  isUploading={isUploading}
                />
              ))}
            </div>
          </div>
        )}

        {/* Optional Documents */}
        {optionalDocs.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-muted-foreground" />
              경력증빙
              <span className="text-xs font-normal text-muted-foreground">
                (해당 없는 서류는 면제 요청 가능)
              </span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {optionalDocs.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  submission={doc}
                  onSubmit={(templateId, files) => submitMutation.mutate({ templateId, files })}
                  onResubmit={(submissionId, files) => resubmitMutation.mutate({ submissionId, files })}
                  onWithdraw={(id) => withdrawMutation.mutate(id)}
                  onRequestExemption={(s) => setExemptionTarget(s)}
                  onCancelExemptionRequest={(id) => cancelExemptionMutation.mutate(id)}
                  isUploading={isUploading}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {submissions.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileCheck className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">제출할 서류가 없습니다.</p>
            </CardContent>
          </Card>
        )}

        {/* Continue Button */}
        {allMandatoryDone && (
          <div className="flex justify-center pt-4">
            <Button size="lg" onClick={handleContinue} className="gap-2">
              시스템으로 진입
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* 면제 요청 다이얼로그 */}
      <Dialog open={!!exemptionTarget} onOpenChange={() => { setExemptionTarget(null); setExemptionReason(""); }}>
        <DialogContent className="sm:w-[calc(100%-2rem)] max-w- mx-auto[400px]">
          <DialogHeader>
            <DialogTitle>면제 요청</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm">
              <span className="font-medium">{exemptionTarget?.template_name}</span> 서류의 면제를 요청합니다.
            </p>
            <p className="text-xs text-muted-foreground">
              관리자 승인 후 면제 처리됩니다. 승인 전까지는 서류 제출이 필요합니다.
            </p>
            <div className="space-y-2">
              <Label>면제 사유 *</Label>
              <Textarea
                value={exemptionReason}
                onChange={(e) => setExemptionReason(e.target.value)}
                placeholder="예: 해당 자격증 없음, 해당 사항 없음 등"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setExemptionTarget(null); setExemptionReason(""); }}>
              취소
            </Button>
            <Button
              onClick={() => {
                if (!exemptionReason.trim()) {
                  return;
                }
                if (exemptionTarget) {
                  exemptionRequestMutation.mutate({
                    submissionId: exemptionTarget.id,
                    reason: exemptionReason,
                  });
                }
              }}
              disabled={!exemptionReason.trim() || exemptionRequestMutation.isPending}
            >
              {exemptionRequestMutation.isPending ? "요청 중..." : "면제 요청"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 1일 연장 요청 다이얼로그 */}
      <Dialog open={extensionDialogOpen} onOpenChange={(open) => { setExtensionDialogOpen(open); if (!open) setExtensionReason(""); }}>
        <DialogContent className="sm:w-[calc(100%-2rem)] max-w- mx-auto[400px]">
          <DialogHeader>
            <DialogTitle>서류 제출 1일 연장 요청</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
              <p className="text-sm text-amber-800">
                서류 제출 마감일이 경과되었습니다. 1일 연장을 요청하시면 내일까지 건너뛰기가 가능합니다.
              </p>
            </div>
            <div className="space-y-2">
              <Label>연장 사유 *</Label>
              <Textarea
                value={extensionReason}
                onChange={(e) => setExtensionReason(e.target.value)}
                placeholder="예: 서류 준비 중, 관련 기관 방문 예정 등"
                rows={3}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              연장 요청 시 관리자에게 알림이 발송됩니다.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setExtensionDialogOpen(false); setExtensionReason(""); }}>
              취소
            </Button>
            <Button
              onClick={() => {
                if (!extensionReason.trim()) return;
                extensionMutation.mutate(extensionReason);
              }}
              disabled={!extensionReason.trim() || extensionMutation.isPending}
            >
              {extensionMutation.isPending ? "요청 중..." : "1일 연장 요청"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
